import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  query,
  where,
  orderBy,
  limit,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import {
  TravelTrip,
  StartTripData,
  ManualTripData,
  GeoPoint,
  ArrivalMatch,
  TravelLegType,
  TravelClassification,
} from '../models/TravelTrip';
import { mileageService, MileageRate } from './MileageService';
import { locationVerificationService } from './LocationVerificationService';

// Commute policy inputs (from company.settings). Undefined => Little Walkers'
// default of NOT reimbursing the first/last commute legs.
export interface CommutePolicy {
  reimburseFirstLeg?: boolean;
  reimburseLastLeg?: boolean;
}

/**
 * TravelService — the explicit two-tap travel-capture lifecycle.
 *
 * Trips live in the top-level `travelTrips` collection. A trip is created by
 * "On my way" (startTrip) and closed by "I'm here" (completeTrip). Manual claims
 * (no live capture) are created via createManualTrip and flagged for approval.
 */
class TravelService {
  private travelCollection = collection(db, 'travelTrips');

  /**
   * Default reimbursable/commute classification for a leg, given company policy.
   * Between-client travel is reimbursable; the home<->client commute legs default
   * to non-reimbursable unless the company opts in.
   */
  classifyLeg(legType: TravelLegType, policy?: CommutePolicy): TravelClassification {
    switch (legType) {
      case 'home-to-first':
        return policy?.reimburseFirstLeg ? 'reimbursable' : 'commute';
      case 'last-to-home':
        return policy?.reimburseLastLeg ? 'reimbursable' : 'commute';
      case 'between-clients':
      case 'other':
      default:
        return 'reimbursable';
    }
  }

  /**
   * "On my way to [client]" — capture the true trip origin (live GPS) tied to a
   * scheduled appointment. Returns the new trip id. Status is 'origin-captured'
   * until arrival is recorded.
   */
  async startTrip(data: StartTripData, policy?: CommutePolicy): Promise<string> {
    const now = Timestamp.now();
    const trip: Omit<TravelTrip, 'id'> = {
      companyId: data.companyId,
      rbtId: data.rbtId,
      rbtName: data.rbtName,
      appointmentId: data.appointmentId,
      clientId: data.clientId,
      clientName: data.clientName,
      origin: data.origin,
      provenance: 'live-captured',
      requiresApproval: false,
      legType: data.legType,
      classification: this.classifyLeg(data.legType, policy),
      status: 'origin-captured',
      createdAt: now,
      updatedAt: now,
    };
    const docRef = await addDoc(this.travelCollection, this.stripUndefined(trip));
    return docRef.id;
  }

  /**
   * "I'm here" — record arrival, validate against the destination's expected
   * (authorized) location, compute Haversine distance from origin, resolve the
   * effective-dated rate, and compute reimbursement (only when the leg is
   * reimbursable). Closes the trip as 'completed'.
   */
  async completeTrip(
    tripId: string,
    arrival: GeoPoint,
    opts: {
      expectedLatitude?: number | null;
      expectedLongitude?: number | null;
      toleranceMiles?: number;
      mileageRates?: MileageRate[];
    } = {}
  ): Promise<TravelTrip> {
    const trip = await this.getTripById(tripId);
    if (!trip) throw new Error('Trip not found');
    if (!trip.origin) {
      // No origin capture => no auto mileage (forfeit). The only way to claim
      // without live capture is the manual path (createManualTrip).
      throw new Error('Cannot complete a trip that has no captured origin');
    }

    // Distance origin -> arrival (straight-line; see MileageService).
    const distance = mileageService.calculateDistance(
      trip.origin.latitude,
      trip.origin.longitude,
      arrival.latitude,
      arrival.longitude
    );

    // Arrival-match against the destination's expected coordinates, when known.
    let arrivalMatch: ArrivalMatch | undefined;
    if (
      opts.expectedLatitude != null &&
      opts.expectedLongitude != null
    ) {
      const result = locationVerificationService.verifyLocation(
        arrival.latitude,
        arrival.longitude,
        opts.expectedLatitude,
        opts.expectedLongitude,
        undefined,
        opts.toleranceMiles
      );
      arrivalMatch = {
        matched: result.isVerified,
        distance: result.distance,
        maxAllowedDistance: result.maxAllowedDistance,
        expectedLatitude: opts.expectedLatitude,
        expectedLongitude: opts.expectedLongitude,
      };
    }

    // Rate in effect on the arrival date; reimbursement only for reimbursable legs.
    const rateApplied = mileageService.resolveRateForDate(
      opts.mileageRates,
      arrival.timestamp.toDate()
    );
    const reimbursementAmount =
      trip.classification === 'reimbursable'
        ? Math.round(distance * rateApplied * 100) / 100
        : 0;

    const updates = this.stripUndefined({
      arrival,
      arrivalMatch,
      distance,
      rateApplied,
      reimbursementAmount,
      status: 'completed' as const,
      updatedAt: Timestamp.now(),
    });

    await updateDoc(doc(this.travelCollection, tripId), updates);
    return { ...trip, ...updates } as TravelTrip;
  }

  /**
   * Manual (after-the-fact) mileage claim — no live capture. Requires a reason and
   * is flagged for supervisor approval (the approval gating itself is Phase 2).
   */
  async createManualTrip(
    data: ManualTripData,
    mileageRates?: MileageRate[],
    policy?: CommutePolicy
  ): Promise<string> {
    if (!data.reason || !data.reason.trim()) {
      throw new Error('A reason is required for a manual mileage claim');
    }
    const now = Timestamp.now();
    const classification =
      data.classification || this.classifyLeg(data.legType, policy);
    const rateApplied = mileageService.resolveRateForDate(
      mileageRates,
      data.travelDate
    );
    const reimbursementAmount =
      classification === 'reimbursable'
        ? Math.round(data.distance * rateApplied * 100) / 100
        : 0;

    const trip: Omit<TravelTrip, 'id'> = {
      companyId: data.companyId,
      rbtId: data.rbtId,
      rbtName: data.rbtName,
      appointmentId: data.appointmentId,
      clientId: data.clientId,
      clientName: data.clientName,
      provenance: 'manual',
      manualReason: data.reason.trim(),
      requiresApproval: true,
      legType: data.legType,
      classification,
      distance: data.distance,
      rateApplied,
      reimbursementAmount,
      status: 'completed',
      createdAt: now,
      updatedAt: now,
    };
    const docRef = await addDoc(this.travelCollection, this.stripUndefined(trip));
    return docRef.id;
  }

  /**
   * The RBT's current in-progress trip ("On my way" tapped, not yet arrived), if
   * any. Used so the UI knows whether to show "I'm here" vs "On my way".
   */
  async getActiveTrip(rbtId: string): Promise<TravelTrip | null> {
    const q = query(
      this.travelCollection,
      where('rbtId', '==', rbtId),
      where('status', '==', 'origin-captured'),
      orderBy('createdAt', 'desc'),
      limit(1)
    );
    const snap = await getDocs(q);
    if (snap.empty) return null;
    const d = snap.docs[0];
    return { id: d.id, ...d.data() } as TravelTrip;
  }

  async getTripById(tripId: string): Promise<TravelTrip | null> {
    const snap = await getDoc(doc(this.travelCollection, tripId));
    if (!snap.exists()) return null;
    return { id: snap.id, ...snap.data() } as TravelTrip;
  }

  /**
   * Completed trips for an RBT within a period — the source of truth for a
   * timesheet's mileage (replacing the old consecutive-session inference).
   */
  async getTripsByRbtAndPeriod(
    rbtId: string,
    periodStart: Date,
    periodEnd: Date
  ): Promise<TravelTrip[]> {
    const q = query(
      this.travelCollection,
      where('rbtId', '==', rbtId),
      where('status', '==', 'completed'),
      orderBy('createdAt', 'desc')
    );
    const snap = await getDocs(q);
    const startMs = periodStart.getTime();
    const endMs = periodEnd.getTime();
    return snap.docs
      .map((d) => ({ id: d.id, ...d.data() } as TravelTrip))
      .filter((t) => {
        const ms = t.createdAt.toMillis();
        return ms >= startMs && ms <= endMs;
      });
  }

  async cancelTrip(tripId: string): Promise<void> {
    await updateDoc(doc(this.travelCollection, tripId), {
      status: 'cancelled',
      updatedAt: Timestamp.now(),
    });
  }

  // Firestore rejects undefined field values; drop them before writing.
  private stripUndefined<T extends Record<string, any>>(obj: T): T {
    const out: Record<string, any> = {};
    for (const [k, v] of Object.entries(obj)) {
      if (v !== undefined) out[k] = v;
    }
    return out as T;
  }
}

export const travelService = new TravelService();
